import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, formatApiError } from "./AuthContext";
import { ArrowLeft, UploadCloud, Eye, Edit3 } from "lucide-react";
import MarkdownToolbar from "../components/MarkdownToolbar";
import MarkdownContent from "../components/MarkdownContent";

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const AUDIENCES = ["All", "Grandparents", "Parents", "Little Ones"];

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  image: "",
  read_time: "5 min read",
  category: "Reflection",
  audience: "All",
};

export default function AdminArticleEditor() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug;
  const { API, authHeaders } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [mode, setMode] = useState("write"); // write | preview

  useEffect(() => {
    if (isNew) return;
    axios
      .get(`${API}/articles/${routeSlug}`)
      .then(({ data }) => {
        setForm({
          slug: data.slug,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          image: data.image,
          read_time: data.read_time,
          category: data.category,
          audience: data.audience || "All",
        });
        setSlugTouched(true);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [API, routeSlug, isNew]);

  const update = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "title" && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      // Relative path so the same record works on any domain.
      update("image", data.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (isNew) {
        await axios.post(`${API}/admin/articles`, payload, { headers: authHeaders });
      } else {
        await axios.put(`${API}/admin/articles/${routeSlug}`, payload, { headers: authHeaders });
      }
      navigate("/admin/articles");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div data-testid="article-editor">
      <Link
        to="/admin/articles"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary editorial-link mb-8"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to articles
      </Link>

      <h1 className="font-serif text-4xl sm:text-5xl font-light">
        {isNew ? "New essay" : "Edit essay"}
      </h1>

      <form onSubmit={onSubmit} className="mt-10 space-y-8">
        <Field label="Title">
          <input
            data-testid="field-title"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            required
            className="quiet-input"
          />
        </Field>

        <Field label="Slug" hint="The URL piece. Auto-filled from the title.">
          <input
            data-testid="field-slug"
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              update("slug", slugify(e.target.value));
            }}
            required
            className="quiet-input"
          />
        </Field>

        <div className="grid sm:grid-cols-3 gap-8">
          <Field label="Category" hint="e.g. Reflection, Boundaries, Connection.">
            <input
              data-testid="field-category"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              required
              className="quiet-input"
            />
          </Field>
          <Field label="Audience" hint="Drives which journal filter shows this essay.">
            <select
              data-testid="field-audience"
              value={form.audience}
              onChange={(e) => update("audience", e.target.value)}
              className="quiet-input bg-transparent"
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </Field>
          <Field label="Read time">
            <input
              data-testid="field-readtime"
              value={form.read_time}
              onChange={(e) => update("read_time", e.target.value)}
              required
              placeholder="e.g. 5 min read"
              className="quiet-input"
            />
          </Field>
        </div>

        <Field label="Excerpt" hint="A one-sentence whisper of the essay.">
          <textarea
            data-testid="field-excerpt"
            value={form.excerpt}
            onChange={(e) => update("excerpt", e.target.value)}
            required
            rows={2}
            className="quiet-input resize-none"
          />
        </Field>

        <Field label="Cover image">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <input
              data-testid="field-image"
              type="text"
              value={form.image}
              onChange={(e) => update("image", e.target.value)}
              placeholder="https://… or /api/media/… or upload below"
              className="quiet-input flex-1"
            />
            <label className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border text-sm text-foreground/80 hover:bg-secondary cursor-pointer">
              <UploadCloud size={16} strokeWidth={1.5} />
              {uploading ? "Uploading…" : "Upload"}
              <input
                data-testid="upload-image"
                type="file"
                accept="image/*"
                onChange={onUpload}
                className="hidden"
              />
            </label>
          </div>
          {form.image && (
            <img
              src={form.image}
              alt=""
              className="mt-4 w-full max-w-sm h-48 object-cover rounded-2xl"
            />
          )}
        </Field>

        <Field
          label="Essay"
          hint="Markdown: **bold**, *italic*, # heading, > pull quote, - bullets, --- divider, two spaces + Enter for a line break."
        >
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              data-testid="mode-write"
              onClick={() => setMode("write")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] ${
                mode === "write" ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70"
              }`}
            >
              <Edit3 size={13} strokeWidth={1.5} /> Write
            </button>
            <button
              type="button"
              data-testid="mode-preview"
              onClick={() => setMode("preview")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] ${
                mode === "preview" ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70"
              }`}
            >
              <Eye size={13} strokeWidth={1.5} /> Preview
            </button>
          </div>

          {mode === "write" ? (
            <>
              <MarkdownToolbar
                textareaRef={contentRef}
                value={form.content}
                onChange={(v) => update("content", v)}
              />
              <textarea
                ref={contentRef}
                data-testid="field-content"
                value={form.content}
                onChange={(e) => update("content", e.target.value)}
                required
                rows={20}
                className="w-full bg-transparent border border-border rounded-2xl p-5 text-base leading-relaxed font-light text-foreground/90 focus:outline-none focus:border-primary font-mono"
                spellCheck
              />
            </>
          ) : (
            <div className="mt-3 bg-background border border-border rounded-2xl p-6 sm:p-10">
              <MarkdownContent withDropCap>{form.content}</MarkdownContent>
            </div>
          )}
        </Field>

        {error && <p className="text-sm italic font-serif text-destructive">{error}</p>}

        <div className="flex items-center gap-6 pt-4">
          <button
            data-testid="save-article"
            type="submit"
            disabled={saving}
            className="px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? "Saving…" : isNew ? "Publish essay" : "Save changes"}
          </button>
          <Link
            to="/admin/articles"
            className="text-sm tracking-wide text-foreground/70 hover:text-foreground editorial-link"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </label>
      <div className="mt-2">{children}</div>
      {hint && <p className="mt-2 text-xs italic font-serif text-muted-foreground">{hint}</p>}
    </div>
  );
}
